<script>
import stripeMixin from '@app/mixins/stripe';
import utilsMixin from '@shared/mixins/utils';
import APIMemberships from '@app/services/API/Memberships';
import { required } from 'vuelidate/lib/validators';

export default {
  mixins: [stripeMixin, utilsMixin],
  validations: {
    newPrice: {
      amount: { required },
      frequency: { required },
      discount_price: {
        discountPriceLessThanAmount(value) {
          if (!value) return true;
          return parseFloat(value) < parseFloat(this.newPrice.amount);
        },
      },
    },
  },
  forms: {
    amount: {
      label: 'Prix (TTC)',
      type: 'number',
      column: 'is-6',
      inputAttrs: {
        min: 1,
        max: 10000,
        step: 0.01,
        required: true,
        placeholder: 'ex: 29.99',
      },
    },
    discount_price: {
      label: 'Prix promotionnel (TTC) (facultatif)',
      type: 'number',
      column: 'is-6',
      inputAttrs: {
        min: 1,
        max: 10000,
        step: 0.01,
        placeholder: 'ex: 19.99',
      },
      errors: [
        {
          name: 'discountPriceLessThanAmount',
          message: 'Le prix promotionnel doit être inférieur au prix normal',
        },
      ],
    },
    discount_price_reason: {
      label: 'Raison de la promotion',
      type: 'text',
      column: 'is-12',
      inputAttrs: {
        maxlength: 64,
        hasCounter: false,
      },
      notification:
        '<p>Vous pouvez ajouter une raison à votre promotion. Cela renforce son efficacité et sa légitimité.</p><p>Elle sera affichée en dessous du prix promotionnel, sur votre page d\'abonnement.<br>Exemple : "Promotion de noël" ou "Prix de lancement"</p>',
      notificationClass: 'content',
    },
    frequency: {
      label: 'Fréquence de facturation',
      type: 'select',
      column: 'is-12',
      options: {
        MONTHLY: 'Mensuel',
        YEARLY: 'Annuel',
      },
      inputAttrs: {
        required: true,
      },
      notification:
        'Pour des fréquences avancées (ex: trimestriel), nous lierons bientôt interval/interval_count. Pour l’instant: Mensuel ou Annuel.',
    },
    rate_vat: {
      label: '% de TVA (inclus dans le prix)',
      type: 'number',
      column: 'is-12',
      inputAttrs: {
        min: 0,
        max: 100,
        step: 0.01,
      },
    },
  },
  mounted() {
    this.$options.forms.rate_vat.inputAttrs.placeholder = `Par défaut : ${this.authStore.default_vat}%`;
  },
  head() {
    return {
      title: `Prix - ${
        this.currentMembership ? this.currentMembership.name : 'Plan'
      }`,
    };
  },
  data() {
    return {
      isLoading: false,
      isCreating: false,
      isCreateModalOpen: false,
      newPrice: {
        amount: '',
        frequency: 'MONTHLY',
        rate_vat: '',
        discount_price: '',
        discount_price_reason: '',
      },
    };
  },
  computed: {
    currentMembership() {
      return this.$store.state.memberships.current;
    },
    authStore() {
      return this.$store.getters['auth/store'];
    },
    prices() {
      return this.currentMembership?.prices || [];
    },
    activePrices() {
      return this.prices.filter((price) => price.status === 'active');
    },
    archivedPrices() {
      return this.prices.filter((price) => price.status === 'archived');
    },
    priceSections() {
      const sections = [];

      if (this.activePrices.length) {
        sections.push({
          key: 'active',
          title: null,
          prices: this.activePrices,
        });
      }

      if (this.archivedPrices.length) {
        sections.push({
          key: 'archived',
          title: 'Prix archivés',
          prices: this.archivedPrices,
        });
      }

      return sections;
    },
    activePricesCount() {
      return this.activePrices.length;
    },
    hasDiscountPrice() {
      return !Number.isNaN(parseFloat(this.newPrice.discount_price));
    },
  },

  methods: {
    intervalBadge(price) {
      return this.formatIntervalShort(
        price,
        (this.$i18n && this.$i18n.locale) || 'fr',
      );
    },
    openCreateModal() {
      this.isCreateModalOpen = true;
      this.newPrice = {
        amount: '',
        frequency: 'MONTHLY',
        rate_vat: '',
        discount_price: '',
        discount_price_reason: '',
      };
    },
    closeCreateModal() {
      this.isCreateModalOpen = false;
      this.newPrice = {
        amount: '',
        frequency: 'MONTHLY',
        rate_vat: '',
        discount_price: '',
        discount_price_reason: '',
      };
    },
    async createPrice() {
      if (!this.dataIsValid(this.isCreating)) return;

      this.isCreating = true;
      try {
        const {
          amount,
          rate_vat,
          discount_price,
          discount_price_reason,
          frequency,
        } = this.newPrice;
        const payload = {
          amount,
          rate_vat: rate_vat || undefined,
          discount_price: discount_price || undefined,
          discount_price_reason: discount_price_reason || undefined,
          interval: frequency === 'YEARLY' ? 'year' : 'month',
          interval_count: 1,
        };
        await APIMemberships.createPrice(this.currentMembership.uuid, payload);
        await this.$store.dispatch(
          'memberships/find',
          this.currentMembership.uuid,
        );
        this.$showMessage.success('Prix créé avec succès');
        this.closeCreateModal();
      } catch (error) {
        this.handleStripeError(error);
      } finally {
        this.isCreating = false;
      }
    },
    async togglePriceStatus(price, action) {
      const loader = this.$buefy.loading.open();
      try {
        if (action === 'activate') {
          await APIMemberships.activatePrice(price.uuid);
        } else if (action === 'archive') {
          await APIMemberships.archivePrice(price.uuid);
        }

        await this.$store.dispatch(
          'memberships/find',
          this.currentMembership.uuid,
        );

        let message;
        if (action === 'activate') {
          message = 'désarchivé';
        } else if (action === 'archive') {
          message = 'archivé';
        }

        this.$showMessage.success(`Prix ${message} avec succès`);
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Erreur lors de la mise à jour du prix';
        this.$showMessage.error(errorMessage);
      } finally {
        loader.close();
      }
    },
    deletePrice(price) {
      this.$buefy.dialog.confirm({
        message:
          'Attention ! Cette action est définitive.<br>Ce prix sera supprimé uniquement s\'il n\'a aucun abonné.',
        type: 'is-danger',
        focusOn: 'cancel',
        onConfirm: async() => {
          const loader = this.$buefy.loading.open();
          try {
            await APIMemberships.deletePrice(price.uuid);
            await this.$store.dispatch(
              'memberships/find',
              this.currentMembership.uuid,
            );
            this.$showMessage.success('Prix supprimé avec succès');
          } catch (error) {
            const errorMessage = error.response?.data?.message
              || (error.response?.status === 422
                ? 'Impossible de supprimer ce prix car il a des abonnés'
                : 'Erreur lors de la suppression du prix');

            this.$showMessage.error(errorMessage);
          } finally {
            loader.close();
          }
        },
      });
    },
    formatCurrency(amount) {
      return this.$options.filters.formatPrice(amount);
    },
    priceHasDiscount(price) {
      return (
        price.discount_price
        && price.discount_price > 0
        && price.discount_price < price.amount
      );
    },
    getPriceStatusColor(price) {
      if (price.status === 'archived') return 'is-light';
      if (price.status === 'active') return 'is-success';
      return 'is-warning';
    },
    getPriceStatusText(price) {
      if (price.status === 'archived') return 'Archivé';
      if (price.status === 'active') return 'Actif';
      return 'Inactif';
    },
  },
};
</script>

<template>
  <div>
    <AppFeatureWrapper>
      <template #title>
        Tarifs de votre plan d'abonnement
      </template>
      <template #content>
        <p>
          Gérez les différents prix de votre plan d'abonnement. Vous pouvez
          créer plusieurs prix avec des fréquences différentes et les
          activer/désactiver selon vos besoins.
        </p>
      </template>

      <template #default>
        <div class="column is-8 is-relative">
          <div class="is-flex is-justify-content-space-between is-align-items-center mb-4">
            <h3 class="title is-5 mb-0">
              Prix actifs
            </h3>
            <b-button type="is-primary" icon-left="plus" @click="openCreateModal">
              Créer un nouveau prix
            </b-button>
          </div>

          <!-- Prices -->
          <div v-if="prices.length === 0" class="box has-text-centered">
            <p>
              Aucun prix. Créez votre premier prix pour commencer à vendre
              votre abonnement.
            </p>
          </div>

          <div v-else>
            <div v-for="section in priceSections" :key="section.key" class="price-section">
              <h3 v-if="section.title" class="title is-5 mt-6 mb-3">
                {{ section.title }}
              </h3>

              <div v-for="price in section.prices" :key="price.uuid"
                class="box is-flex is-justify-content-space-between is-align-items-center">
                <div>
                  <h4 class="title is-5 mb-2">
                    <template v-if="priceHasDiscount(price)">
                      <span class="has-text-grey-dark" style="text-decoration: line-through;">
                        {{ formatCurrency(price.amount) }}
                      </span>
                      <span class="has-text-danger ml-2">
                        {{ formatCurrency(price.discount_price) }}
                      </span>
                    </template>
                    <template v-else>
                      {{ formatCurrency(price.amount) }}
                    </template>
                    <span class="tag is-light ml-2">
                      {{ intervalBadge(price) }}
                    </span>
                    <span :class="`tag ${getPriceStatusColor(price)} ml-2`">
                      {{ getPriceStatusText(price) }}
                    </span>
                  </h4>
                  <p v-if="priceHasDiscount(price) && price.discount_price_reason"
                    class="is-size-7 has-text-danger mb-2">
                    {{ price.discount_price_reason }}
                  </p>
                  <p v-if="price.rate_vat" class="is-size-8">
                    TVA : {{ price.rate_vat }}%
                  </p>
                  <p class="is-size-8">
                    Créé le {{ $moment(price.created_at).format("DD/MM/YYYY") }}
                  </p>
                  <p v-if="price.subscribers_count" class="is-size-7 has-text-grey">
                    {{ price.subscribers_count }} abonné(s) actif(s)
                  </p>
                </div>
                <div>
                  <b-dropdown position="is-bottom-left" aria-role="menu">
                    <template #trigger>
                      <b-button type="is-text" icon-left="ellipsis-h" />
                    </template>
                    <b-dropdown-item v-if="price.status === 'active'" aria-role="menuitem"
                      @click="togglePriceStatus(price, 'archive')">
                      Archiver
                    </b-dropdown-item>
                    <b-dropdown-item v-if="price.status === 'archived'" aria-role="menuitem"
                      @click="togglePriceStatus(price, 'activate')">
                      Désarchiver
                    </b-dropdown-item>

                    <b-dropdown-item v-if="!price.has_subscribers" aria-role="menuitem" @click="deletePrice(price)">
                      <span class="has-text-danger">Supprimer</span>
                    </b-dropdown-item>
                  </b-dropdown>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </AppFeatureWrapper>

    <!-- Create Price Modal -->
    <b-modal :active="isCreateModalOpen" has-modal-card @close="closeCreateModal">
      <form class="modal-card" @submit.prevent="createPrice">
        <header class="modal-card-head">
          <p class="modal-card-title">Créer un nouveau prix</p>
        </header>
        <section class="modal-card-body">
          <div class="columns is-multiline">
            <div v-for="(field, key) in $options.forms" :key="key" v-show="
                key != 'discount_price_reason' ||
                  (key == 'discount_price_reason' && hasDiscountPrice)
  " class="column" :class="field.column">
              <BaseField v-model="newPrice[key]" :field="field" :v="$v.newPrice[key]" />
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <b-button type="is-primary" native-type="submit" :loading="isCreating">
            Créer le prix
          </b-button>
          <b-button @click="closeCreateModal">
            Annuler
          </b-button>
        </footer>
      </form>
    </b-modal>
  </div>
</template>
